import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import type {
  BigQueryUsageExecution,
  BigQueryUsageExecutionSortBy,
  BigQueryUsageSortOrder,
} from "@/features/bigquery-usage/api/bigquery-usage.api";
import { getPublishedDisplay } from "@/features/events/lib/event-row-display";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { PRIMARY_LIST_MAX_HEIGHT, STICKY_HEAD_CLASS } from "@/features/llm-usage/lib/table-scroll";
import { useInfiniteScrollSentinel } from "@/features/overview/hooks/useInfiniteScrollSentinel";
import { formatBytesCompact, formatBytesExact } from "../lib/bytes";
import { formatSqlPreview, shortFingerprint } from "../lib/fingerprint-display";
import { humanizeBigQuerySlug } from "../lib/slug-labels";
import { splitSqlNote } from "../lib/sql-note";
import { SqlLine } from "./SqlLine";

export interface BigQueryExecutionsTableProps {
  /** One page-set of the cursor-paginated list, already flattened, in the order the server returned. */
  rows: BigQueryUsageExecution[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isStale: boolean;
  sortBy: BigQueryUsageExecutionSortBy;
  sortOrder: BigQueryUsageSortOrder;
  /** Reported upward: the sort lives in the URL and the ordering is the server's. */
  onSort: (column: BigQueryUsageExecutionSortBy) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  selectedRowId: string | null;
  onSelectRow: (row: BigQueryUsageExecution) => void;
}

const HEADER_BASE =
  "whitespace-nowrap border-b border-border px-3 py-2 text-left text-[11px] font-normal uppercase tracking-wider text-muted-foreground";
const STATEMENT_HEADER_CLASS = cn(HEADER_BASE, "w-full");
const NUMERIC_HEADER_CLASS = cn(HEADER_BASE, "text-right");
const CELL_CLASS = "px-3 py-2 text-xs text-foreground";
// See the note in `BigQueryTenantTable`: without this a squeezed numeric column
// breaks a value between its number and its unit.
const NUMERIC_CELL_CLASS = cn(CELL_CLASS, "text-right tabular-nums whitespace-nowrap");
const SKELETON_ROWS = 5;
const COLUMN_COUNT = 8;
const DASH = "—";

/** A row is one line, so the preview gets less room here than in the breakdown. */
const ROW_PREVIEW_MAX_LENGTH = 64;
/** A notch under the row's line height, matching the tenant breakdown's mark. */
const TENANT_MARK_SIZE = 16;

/**
 * Which tenant a row belongs to, in words.
 *
 * Both specials arrive with a null id, so `kind` is the only thing that tells
 * them apart: `platform` is work no tenant asked for, `unattributed` is work
 * whose caller was never recorded. The icon is a second signal, not the signal —
 * an operator who cannot see the muted hue still reads the label. Neither
 * carries a logo: a mark would give a category a brand it does not have.
 *
 * A named tenant gets its own mark beside its name. A tenant deleted since the
 * query ran has no name left, so the row keeps the id it can still be traced
 * by — initials derived from a cuid would name nothing.
 */
function TenantCell({ row }: { row: BigQueryUsageExecution }) {
  const { t } = useTranslation();

  if (row.tenantKind === "platform") {
    return (
      <span className="flex items-center gap-1.5">
        <Icon name="shield" size="xs" aria-hidden />
        {t("admin.bigQueryUsage.tenantTable.platform", "Platform")}
      </span>
    );
  }

  if (row.tenantKind === "unattributed") {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon name="info" size="xs" aria-hidden />
        {t("admin.bigQueryUsage.tenantTable.unattributed", "Unattributed")}
      </span>
    );
  }

  if (row.tenantName == null) {
    return (
      <span className="truncate font-mono text-[11px]" title={row.tenantId ?? undefined}>
        {row.tenantId ?? DASH}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5" title={row.tenantId ?? undefined}>
      <TenantMark
        name={row.tenantName}
        logoUrl={row.logoUrl}
        brandColor={row.brandColor}
        size={TENANT_MARK_SIZE}
      />
      <span className="truncate">{row.tenantName}</span>
    </span>
  );
}

/**
 * The individual executions behind every aggregate above: zoom level four.
 *
 * The list renders whether or not a narrowing is active — unfiltered it is the
 * window's most recent queries, which is what an admin scanning for something
 * unusual opens the tab for. A drill-in narrows it in place.
 *
 * Nothing here sorts or filters: the list holds one page of a cursor-paginated
 * read, so ordering what it happens to have fetched would mislabel a page as a
 * ranking. A header click goes to the server and comes back as new rows.
 */
export function BigQueryExecutionsTable({
  rows,
  isLoading,
  isError,
  onRetry,
  isStale,
  sortBy,
  sortOrder,
  onSort,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  selectedRowId,
  onSelectRow,
}: BigQueryExecutionsTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const sort: SortState<BigQueryUsageExecutionSortBy> = { key: sortBy, dir: sortOrder };

  // Pointer-free paging is the button and the end-of-results line below; the
  // sentinel is the shortcut for a mouse, never the only way down the list.
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
    isError,
  });

  const title = t("admin.bigQueryUsage.executionsTable.title", "Individual queries");

  return (
    <section
      aria-label={title}
      className="flex min-h-0 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card"
    >
      <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className={cn(PRIMARY_LIST_MAX_HEIGHT, "overflow-y-auto")}>
        <table className="w-full border-collapse">
          <caption className="sr-only">
            {t("admin.bigQueryUsage.executionsTable.caption", "Individual BigQuery queries")}
          </caption>
          <thead className={STICKY_HEAD_CLASS}>
            <tr className="group">
              <SortHeader
                id="createdAt"
                label={t("admin.bigQueryUsage.executionsTable.time", "Time")}
                sort={sort}
                onSort={onSort}
                className={HEADER_BASE}
              />
              <th scope="col" className={HEADER_BASE}>
                {t("admin.bigQueryUsage.featureTable.feature", "Feature")}
              </th>
              <th scope="col" className={HEADER_BASE}>
                {t("admin.bigQueryUsage.tenantTable.tenant", "Tenant")}
              </th>
              <th scope="col" className={STATEMENT_HEADER_CLASS}>
                {t("admin.bigQueryUsage.fingerprintTable.statement", "Statement")}
              </th>
              <SortHeader
                id="bytesBilled"
                label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
                sort={sort}
                onSort={onSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="durationMs"
                label={t("admin.bigQueryUsage.executionsTable.duration", "Duration")}
                sort={sort}
                onSort={onSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <th scope="col" className={HEADER_BASE}>
                {t("admin.bigQueryUsage.table.cache", "Cache")}
              </th>
              <th scope="col" className={HEADER_BASE}>
                {t("admin.bigQueryUsage.executionsTable.job", "Job")}
              </th>
            </tr>
          </thead>
          <tbody
            className={cn("transition-opacity duration-200", isStale && "opacity-40")}
            aria-busy={isStale || undefined}
          >
            {isError ? (
              <tr>
                <td className={cn(CELL_CLASS, "py-6 text-center")} colSpan={COLUMN_COUNT}>
                  <span className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
                      {t(
                        "admin.bigQueryUsage.executionsTable.error",
                        "Could not load the individual queries.",
                      )}
                    </span>
                    <Button variant="outline" size="sm" onClick={onRetry}>
                      {t("admin.bigQueryUsage.retry", "Try again")}
                    </Button>
                  </span>
                </td>
              </tr>
            ) : isLoading && rows.length === 0 ? (
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className={CELL_CLASS} colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-4 w-full rounded-sm bg-foreground/8" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  className={cn(CELL_CLASS, "py-6 text-center text-muted-foreground")}
                  colSpan={COLUMN_COUNT}
                >
                  {t(
                    "admin.bigQueryUsage.executionsTable.empty",
                    "No individual queries in this window",
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const ran = getPublishedDisplay(row.createdAt, locale);
                const isSelected = row.id === selectedRowId;
                const hasFailed = row.errorReason != null;
                const featureLabel = humanizeBigQuerySlug(row.feature);
                const sql = splitSqlNote(row.sqlPreview);
                // A preview whose 200-character cap landed inside the leading
                // comment stored no SQL at all, so the note is all there is.
                const hasNoteOnly = sql.statement.length === 0;
                const statementLine = hasNoteOnly ? (sql.note ?? "") : sql.statement;

                return (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    aria-label={t(
                      "admin.bigQueryUsage.executionsTable.rowLabel",
                      "Open the {{feature}} query from {{time}}",
                      { feature: featureLabel, time: `${ran.date} ${ran.time}` },
                    )}
                    data-state={isSelected ? "selected" : undefined}
                    onClick={() => onSelectRow(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectRow(row);
                      }
                    }}
                    className={cn(
                      "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40",
                      "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                      hasFailed && "bg-error-subtle",
                      isSelected && "bg-primary/5 hover:bg-primary/5",
                    )}
                  >
                    <td className={cn(CELL_CLASS, "whitespace-nowrap")}>
                      <div className="tabular-nums">{ran.time}</div>
                      <div className="text-[11px] text-muted-foreground">{ran.date}</div>
                    </td>
                    {/* The slug is what the logs and the API carry, so it stays
                        on the cell the derived label replaced. */}
                    <td className={cn(CELL_CLASS, "max-w-40 truncate")} title={row.feature}>
                      {featureLabel}
                    </td>
                    <td className={cn(CELL_CLASS, "max-w-40")}>
                      <TenantCell row={row} />
                    </td>
                    {/* Only the statement, never the note in front of it: this
                        column is the narrowest on the table, and a leading
                        comment fills it before the SQL gets a character. The
                        note is on the row's drawer. */}
                    <td className={cn(CELL_CLASS, "max-w-0")}>
                      <SqlLine
                        line={formatSqlPreview(statementLine, ROW_PREVIEW_MAX_LENGTH)}
                        className={cn(
                          "block max-w-full truncate font-mono text-[11px]",
                          hasNoteOnly && "text-muted-foreground",
                        )}
                        title={row.sqlPreview}
                      />
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {shortFingerprint(row.sqlFingerprint)}
                      </span>
                    </td>
                    {/* A failed query billed nothing it can vouch for, so the
                        cell carries the reason rather than a figure that would
                        be read as spend. */}
                    <td
                      className={cn(NUMERIC_CELL_CLASS, !hasFailed && "font-medium")}
                      title={hasFailed ? undefined : formatBytesExact(row.bytesBilled, locale)}
                    >
                      {hasFailed ? (
                        <span className="flex items-center justify-end gap-1.5 text-error">
                          <Icon name="alert-circle" size="xs" aria-hidden />
                          <span className="shrink-0">
                            {t("admin.bigQueryUsage.executionsTable.failed", "Failed")}
                          </span>
                          <span className="truncate" title={row.errorReason ?? undefined}>
                            {row.errorReason}
                          </span>
                        </span>
                      ) : (
                        formatBytesCompact(row.bytesBilled, locale)
                      )}
                    </td>
                    <td className={NUMERIC_CELL_CLASS}>
                      {formatPreciseDurationMs(row.durationMs)}
                    </td>
                    <td className={cn(CELL_CLASS, "whitespace-nowrap")}>
                      {row.cacheHit ? (
                        <span
                          className="flex items-center gap-1.5"
                          title={t(
                            "admin.bigQueryUsage.executionsTable.cachedTitle",
                            "Answered from BigQuery's result cache",
                          )}
                        >
                          <Icon name="history" size="xs" aria-hidden />
                          {t("admin.bigQueryUsage.executionsTable.cached", "Cached")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("admin.bigQueryUsage.executionsTable.fresh", "Fresh")}
                        </span>
                      )}
                    </td>
                    <td
                      className={cn(
                        CELL_CLASS,
                        "max-w-32 truncate font-mono text-[11px] text-muted-foreground",
                      )}
                      title={row.jobId ?? undefined}
                    >
                      {row.jobId ?? DASH}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* R34: the sentinel is a shortcut for a pointer, so the button and the
            end-of-results line carry the same job for a keyboard. */}
        {!isError && rows.length > 0 ? (
          <div
            ref={sentinelRef}
            className="flex items-center justify-center p-3 text-xs text-muted-foreground"
          >
            {isFetchingNextPage ? (
              <span className="inline-flex items-center gap-2">
                <Icon
                  name="loader"
                  size="xs"
                  className="animate-spin motion-reduce:animate-none"
                  aria-hidden
                />
                {t("admin.bigQueryUsage.executionsTable.loadingMore", "Loading more…")}
              </span>
            ) : hasNextPage ? (
              <Button variant="ghost" size="sm" onClick={onLoadMore}>
                {t("admin.bigQueryUsage.executionsTable.loadMore", "Load more")}
              </Button>
            ) : (
              <span>{t("admin.bigQueryUsage.executionsTable.endOfResults", "End of results")}</span>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
